import React from 'react';
import { ArrowUp, ArrowDown, Eye, Wrench, Circle } from 'lucide-react';
import { formatCurrency } from '../utils/formatters';

const TiresTable = ({ tires, sortBy, sortOrder, onSort, onViewDetails, onInstall }) => {
  // Status labels e cores
  const getStatusInfo = (status) => {
    const statusMap = {
      in_stock: { label: 'Em Estoque', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
      installed: { label: 'Instalado', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
      storage: { label: 'Armazenado', color: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200' },
      damaged: { label: 'Danificado', color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
      disposed: { label: 'Descartado', color: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200' }
    };
    return statusMap[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
  };

  // Categoria labels
  const getCategoryLabel = (category) => {
    const categoryMap = {
      steer: 'Direção',
      drive: 'Tração',
      trailer: 'Reboque',
      all_position: 'Todas Posições'
    };
    return categoryMap[category] || category;
  };

  // Renderizar ícone de ordenação
  const SortIcon = ({ column }) => {
    if (sortBy !== column) {
      return <ArrowUp className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100" />;
    }
    return sortOrder === 'ASC' ? (
      <ArrowUp className="w-3 h-3 text-blue-600 dark:text-blue-400" />
    ) : (
      <ArrowDown className="w-3 h-3 text-blue-600 dark:text-blue-400" />
    );
  };

  // Renderizar cabeçalho clicável
  const TableHeader = ({ column, label, sortable = true }) => {
    return (
      <th
        className={`px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider ${
          sortable ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 group' : ''
        }`}
        onClick={() => sortable && onSort(column)}
      >
        <div className="flex items-center gap-2">
          {label}
          {sortable && <SortIcon column={column} />}
        </div>
      </th>
    );
  };

  // Profundidade com cor baseada no valor
  const TreadDepthCell = ({ depth }) => {
    if (!depth) return <span className="text-gray-400">-</span>;

    const value = parseFloat(depth);
    let colorClass = 'text-green-600 dark:text-green-400 font-medium';

    if (value < 3) {
      colorClass = 'text-red-600 dark:text-red-400 font-bold';
    } else if (value < 5) {
      colorClass = 'text-yellow-600 dark:text-yellow-400 font-medium';
    }

    return <span className={colorClass}>{value.toFixed(1)}mm</span>;
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            <TableHeader column="serial_number" label="Nº Série" />
            <TableHeader column="brand" label="Marca" />
            <TableHeader column="model" label="Modelo" />
            <TableHeader column="size" label="Tamanho" />
            <TableHeader column="category" label="Categoria" sortable={false} />
            <TableHeader column="current_tread_depth_mm" label="Profundidade" />
            <TableHeader column="status" label="Status" />
            <TableHeader column="purchase_price" label="Valor" />
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Ações
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-700 divide-y divide-gray-200 dark:divide-gray-600">
          {tires.map((tire) => {
            const statusInfo = getStatusInfo(tire.status);

            return (
              <tr
                key={tire.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                {/* Nº Série */}
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center">
                    <Circle className="w-3 h-3 text-gray-400 mr-2" />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {tire.serial_number}
                    </span>
                  </div>
                </td>

                {/* Marca */}
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="text-sm text-gray-900 dark:text-white">
                    {tire.brand}
                  </span>
                </td>

                {/* Modelo */}
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {tire.model}
                  </span>
                </td>

                {/* Tamanho */}
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="text-sm font-mono text-gray-900 dark:text-white">
                    {tire.size}
                  </span>
                </td>

                {/* Categoria */}
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {getCategoryLabel(tire.category)}
                  </span>
                </td>

                {/* Profundidade */}
                <td className="px-4 py-3 whitespace-nowrap text-sm">
                  <TreadDepthCell depth={tire.current_tread_depth_mm} />
                </td>

                {/* Status */}
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${statusInfo.color}`}>
                    {statusInfo.label}
                  </span>
                </td>

                {/* Valor */}
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                  {tire.purchase_price ? formatCurrency(parseFloat(tire.purchase_price)) : '-'}
                </td>

                {/* Ações */}
                <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onViewDetails(tire)}
                      className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
                      title="Ver detalhes"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    {(tire.status === 'in_stock' || tire.status === 'storage') && (
                      <button
                        onClick={() => onInstall(tire)}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-200"
                        title="Instalar pneu"
                      >
                        <Wrench className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {tires.length === 0 && (
        <div className="text-center py-12">
          <Circle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">Nenhum pneu encontrado</p>
        </div>
      )}
    </div>
  );
};

export default TiresTable;
